/**
 * The TV's sound decisions, shared by the TV page (sfx.ts, Web Audio) and the
 * Apple TV app (web/tv/appletv/src/sfx.ts, expo-audio). Pure: no audio API,
 * no DOM, no React. The caller supplies `playSound` and feeds the events:
 *
 * - applySnapshot: the phone's `sound` field on every snapshot (who owns
 *   sound, the Settings switches, and any new planned moment).
 * - rollStarted: a new putt event starts rolling (the miss tone).
 * - begin: the replay starts; returns the build cue and the cup to hand the
 *   scene (SceneContent soundCueS / soundCupFt).
 * - cue: the scene reached the cue (onSoundCue): the build rises.
 * - holed: the ball dropped (onBallHoled): the cup drop, then the cheer.
 * - reachedCup: the ball crossed the cup line (onBallReachedCup): a near
 *   miss that slides past resolves (groan-release, or a planned cheer).
 * - settled: the ball stopped (onRollSettled): a near miss that stops, or a
 *   planned sound that never dropped.
 *
 * Rules: packages/putt-scene/src/sound.ts and app/LagKing/SOUNDS.md.
 */
import {
  readTvPuttSoundWire,
  readTvSoundState,
  SfxDirector,
  SFX_ACTIVE,
  sfxAllowed,
  puttSoundContext,
  type BuildCue,
  type PuttSoundContext,
  type SfxName,
  type SfxStep,
  type TvSoundState,
} from '../../../packages/putt-scene/src/sound';

/** Plays one sound now, from `seekMs` into the file, cutting the current one. */
export type TvPlaySound = (name: SfxName, seekMs?: number) => void;
/** Runs `run` after `ms`; returns a cancel. Defaults to setTimeout. */
export type TvSchedule = (run: () => void, ms: number) => () => void;

const defaultSchedule: TvSchedule = (run, ms) => {
  const handle = setTimeout(run, ms);
  return () => clearTimeout(handle);
};

/** What a TV stage knows about the replay it is about to roll. */
export interface TvReplaySoundInput {
  /** The putt event's `sfxPutt` (the phone's context), and `ghost`. */
  event: { sfxPutt?: unknown; ghost?: unknown };
  made: boolean;
  holedAtFt: number | null;
  hideHole: boolean;
  targetFt: number;
  proximityFt: number | null;
  speedFtS: number;
  distanceFt: number;
}

/** The pure part, separate from any audio API so it is unit-tested. */
export class TvSfxController {
  private director = new SfxDirector();
  private state: TvSoundState | null = null;
  private seenMomentId: string | null | undefined = undefined;
  private contexts = new Map<string, PuttSoundContext>();
  private cancels: Array<() => void> = [];
  private sequenceKey: string | null = null;

  private readonly playSound: TvPlaySound;
  private readonly schedule: TvSchedule;
  private readonly active: ReadonlySet<SfxName>;

  /** `active`: the sounds that may play (SFX_ACTIVE: the cup drop only). */
  constructor(playSound: TvPlaySound, schedule: TvSchedule = defaultSchedule, active: ReadonlySet<SfxName> = SFX_ACTIVE) {
    this.playSound = playSound;
    this.schedule = schedule;
    this.active = active;
  }

  /** The TV plays only when the phone handed it sound. */
  owns(): boolean {
    return this.state?.owner === 'tv';
  }

  soundOn(): boolean {
    return this.owns() && this.state?.enabled === true;
  }

  /** Every snapshot: take the phone's settings and any new moment. The
   *  first snapshot's moment is history (a page reload), never replayed. */
  applySnapshot(raw: unknown, nowMs: number): void {
    const state = readTvSoundState(raw);
    this.state = state;
    const moment = state?.moment ?? null;
    const id = moment?.id ?? null;
    if (this.seenMomentId === undefined) {
      this.seenMomentId = id;
      return;
    }
    if (id === null || id === this.seenMomentId) return;
    this.seenMomentId = id;
    if (moment!.scope === 'putt') this.director.plan(moment!.sound, nowMs);
    else this.run('round', [{ name: moment!.sound, delayMs: 0 }]);
  }

  /** A new putt event started rolling on this page: the miss tone. */
  rollStarted(eventId: string, event: { sfx?: unknown }): void {
    if (event.sfx !== 'miss') return;
    const name = this.director.stroke(eventId, { majorMiss: true, isMake: false, gradeShownAtStroke: true });
    if (name) this.run(eventId, [{ name, delayMs: 0 }]);
  }

  /**
   * The replay starts: the putt's sound context (the phone's `sfxPutt`,
   * else the scene's own target and leave) and the build cue to hand the
   * scene (SceneContent soundCueS = cue.cueMs / 1000, soundCupFt = cupFt).
   */
  begin(eventId: string, input: TvReplaySoundInput, nowMs: number): { cue: BuildCue | null; cupFt: number | null } {
    const ctx = puttSoundContext({
      wire: readTvPuttSoundWire(input.event.sfxPutt),
      ghost: input.event.ghost === true,
      made: input.made,
      holedAtFt: input.holedAtFt,
      hideHole: input.hideHole,
      targetFt: input.targetFt,
      proximityFt: input.proximityFt,
    });
    this.contexts.set(eventId, ctx);
    const cue = this.owns()
      ? this.director.begin(eventId, ctx, { speedFtS: input.speedFtS, distanceFt: input.distanceFt }, this.state, nowMs)
      : this.director.begin(eventId, ctx, null, null, nowMs);
    return { cue, cupFt: ctx.cupFt };
  }

  /** The scene reached the build cue (onSoundCue). */
  cue(eventId: string): void {
    this.run(eventId, this.director.cue(eventId));
  }

  /** The ball dropped on this page's replay (onBallHoled): the cup drop,
   *  then the eruption. `cupFt`/`extraCup` serve a caller that never called
   *  begin (an older stage): a 30 ft+ main-cup make still roars. */
  holed(eventId: string, nowMs: number, cupFt: number | null = null, extraCup = false): void {
    if (!this.contexts.has(eventId)) {
      const ctx: PuttSoundContext = { own: false, cupFt, leaveFt: 0, holed: true, extraCup };
      this.contexts.set(eventId, ctx);
      this.director.begin(eventId, ctx, null, null, nowMs);
    }
    this.run(eventId, this.director.holed(eventId, nowMs));
  }

  /** The ball crossed the cup line (onBallReachedCup). */
  reachedCup(eventId: string, nowMs: number): void {
    this.run(eventId, this.director.reachedCup(eventId, nowMs));
  }

  /** The ball stopped (onRollSettled). */
  settled(eventId: string, nowMs: number): void {
    this.run(eventId, this.director.settled(eventId, nowMs));
  }

  private run(key: string, steps: SfxStep[]): void {
    if (steps.length === 0) return;
    if (key !== this.sequenceKey) {
      this.cancels.forEach((cancel) => cancel());
      this.cancels = [];
      this.sequenceKey = key;
    }
    for (const step of steps) {
      if (step.delayMs <= 0) this.play(step.name, step.seekMs);
      else this.cancels.push(this.schedule(() => this.play(step.name, step.seekMs), step.delayMs));
    }
  }

  private play(name: SfxName, seekMs?: number): void {
    if (!this.owns() || !sfxAllowed(name, this.state, this.active)) return;
    this.playSound(name, seekMs);
  }
}
